import {
  type HandoverKind,
  type Party,
  type PartyCandidate,
  type PartyRole,
  partyAddressIsPrefilled,
  partyRoleSchema,
} from './handovers'

/**
 * What the editor does with the people onOffice offers: whether one is already
 * in the protocol, where a new party goes, what taking a candidate writes, and
 * whether a side is a question the agent has to answer.
 *
 * Pure functions, and in the shared package for ONE reason: they decide which
 * person ends up in a legal document, and the web package has no test runner.
 * Here the API's suite covers them. They take and return plain data — no React,
 * no browser globals, and the id generator is passed in.
 */

type CandidateRole = PartyCandidate['role']

/**
 * Whether a candidate already sits in the protocol.
 *
 * By onOffice address id, not by name: a name gets corrected on site ("Meyer",
 * not "Meier") and must not make the person reappear as still on offer.
 *
 * The name comparison is a FALLBACK for drafts written before
 * `sourceAddressId` existed — their prefilled parties carry null, and without
 * it every one of them would be offered a second time. It only ever applies to
 * a party with no source id: one filled from a different onOffice address is a
 * different person, whatever they are called.
 */
export function isInProtocol(candidate: PartyCandidate, parties: Party[]): boolean {
  const name = normaliseName(candidate.name)

  return parties.some(
    (party) =>
      party.sourceAddressId === candidate.addressId ||
      (party.sourceAddressId === null &&
        party.role === candidate.role &&
        name !== '' &&
        normaliseName(party.name) === name),
  )
}

function normaliseName(name: string): string {
  return name.trim().toLocaleLowerCase('de-DE')
}

/**
 * Insert a party where the PDF will print it: directly after the last party of
 * its role. Array order IS document order, so appending would put a second
 * Mieter after the witnesses.
 *
 * With nobody of that role yet, it goes before the first party of a later role
 * in the schema's order (handover, receiver, agent, witness).
 */
export function insertParty(parties: Party[], party: Party): Party[] {
  const lastOfRole = parties.findLastIndex((entry) => entry.role === party.role)
  const rank = (role: PartyRole) => partyRoleSchema.options.indexOf(role)

  const index =
    lastOfRole >= 0
      ? lastOfRole + 1
      : (() => {
          const later = parties.findIndex((entry) => rank(entry.role) > rank(party.role))
          return later >= 0 ? later : parties.length
        })()

  return [...parties.slice(0, index), party, ...parties.slice(index)]
}

/**
 * Take a candidate into the protocol.
 *
 * A BLANK party of the same role is filled rather than joined by a second one —
 * that is the empty slot the Auszug arrives with when the tool would not guess.
 * Its id is kept, because signatures and photos are keyed on it.
 *
 * `bankDetails` is left alone: it is never prefilled, the account for a deposit
 * refund is given at the appointment.
 */
export function takeCandidate(
  parties: Party[],
  candidate: PartyCandidate,
  kind: HandoverKind,
  newId: () => string,
): Party[] {
  const fields = {
    name: candidate.name,
    // Blank for the tenant who is leaving — see `partyAddressIsPrefilled`.
    address: partyAddressIsPrefilled(kind, candidate.role) ? candidate.address : '',
    phone: candidate.phone,
    email: candidate.email,
    sourceAddressId: candidate.addressId,
  }

  const blank = parties.find((party) => party.role === candidate.role && !party.name.trim())
  if (blank) {
    return parties.map((party) => (party.id === blank.id ? { ...party, ...fields } : party))
  }

  return insertParty(parties, {
    id: newId(),
    role: candidate.role,
    bankDetails: '',
    present: true,
    ...fields,
  })
}

/** The candidates of one side that are still on offer. */
export function candidatesOnOffer(
  role: CandidateRole,
  candidates: PartyCandidate[],
  parties: Party[],
): PartyCandidate[] {
  return candidates.filter(
    (candidate) => candidate.role === role && !isInProtocol(candidate, parties),
  )
}

/**
 * Whether this side is a QUESTION the agent has to answer rather than a list
 * of extras: nobody named, nobody suggested, and both a Mieter and a Vormieter
 * on offer.
 *
 * The last condition is the one that matters. "Nobody suggested" alone is also
 * true at an Einzug where only former tenants are linked — the incoming tenant
 * is simply not in onOffice yet — and asking "Wer zieht ein?" over a list of
 * people who moved OUT would be the tool suggesting the wrong answer in the
 * form of a question.
 *
 * Exported because the section places the block by it: a question goes ABOVE
 * the empty card it is about, a list of extras below the people it adds to.
 */
export function isOpenQuestion(
  role: CandidateRole,
  candidates: PartyCandidate[],
  parties: Party[],
): boolean {
  const offered = candidatesOnOffer(role, candidates, parties)
  if (offered.length === 0) return false
  if (parties.some((party) => party.role === role && party.name.trim())) return false
  if (offered.some((candidate) => candidate.suggested)) return false

  const former = offered.some((candidate) => candidate.tenancy === 'former')
  const sitting = offered.some(
    (candidate) => candidate.tenancy === 'current' || candidate.tenancy === 'unknown',
  )
  return former && sitting
}
